import * as React from 'react'
import { debounce, hasValue, parseValue } from '../../../util';
import classNames from 'classnames';
import { ColorPallet, ColorPicker, HorizontalScrollList, IColorPickerProps, IconButton, Input, Label, Loading, Modal, PortalContainer, SearchBox, Tooltip, useDebounce, useToast } from 'uxp/components';
import { FAIcon as FontAwesomeIcon } from '../components/FAIcon';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { IContextProvider } from '../../../uxp';
import { generateIconProp } from '../icon-selector/IconEditor';
import { IContextData, useInitialContextData } from '../../../hooks/useContextData';
import './ui-props.scss';


export const PropertyContainer: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-container'> {props.children} </div>
}

export const PropertyContainerTitle: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-container-title'> {props.children} </div>
}
export const PropertyContainerSubTitle: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-container-sub-title'> {props.children} </div>
}

export const PropertyGroup: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-group'> {props.children} </div>
}

export const PropertyGroupTitle: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-group-title'> {props.children} </div>
}

export const Property: React.FunctionComponent<{ id?: string, styles?: React.CSSProperties, children?: React.ReactNode }> = props => {
    return <div
        className={'wd-ui-property'}
        id={props.id}
        style={props.styles}
    >
        {props.children}
    </div>
}

export const PropertyTitle: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-title'> {props.children} </div>
}

export const PropertySubTitle: React.FunctionComponent<{ children?: React.ReactNode }> = props => {
    return <div className='wd-ui-property-sub-title'> {props.children} </div>
}

// Shared v5 remover for repeatable list items (sheets / tabs / columns / cards).
// Replaces the assorted bespoke black-circle "×" divs each editor rolled its own
// of — matches the migrated canvas field delete (dnd.tsx renderRemoveButton) so
// every remove control across the designer looks identical. Presentational only:
// `onClick` fires the same removal the old div did.
export const RemoveButton: React.FunctionComponent<{ onClick: () => void, className?: string, tooltip?: string }> = props => {
    return <IconButton
        className={classNames('wd-ui-remove-btn', props.className)}
        type="delete"
        variant="danger"
        size="small"
        tooltip={props.tooltip}
        onClick={(e) => {
            e?.preventDefault()
            e?.stopPropagation()
            props.onClick()
        }}
    />
}

// Shared v5 card for a repeatable configuration item. Replaces the per-editor
// grey `rgba(0,0,0,.12)` boxes with a theme-var surface + a top-right RemoveButton
// (rendered only when `onRemove` is given). Structure/spacing is consistent across
// every complex editor; children are the item's own fields, unchanged.
export const PropertyCard: React.FunctionComponent<{ onRemove?: () => void, removeTooltip?: string, className?: string, children?: React.ReactNode }> = props => {
    return <div className={classNames('wd-ui-property-card', props.className)}>
        {props.onRemove && <RemoveButton className='wd-ui-property-card__remove' onClick={props.onRemove} tooltip={props.removeTooltip} />}
        {props.children}
    </div>
}

export function makeColorTransparent(color: string, opacity: number) {
    if (!color) return null
    let m = color.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (m) {
        return `rgba(${m[1]},${m[2]},${m[3]},${opacity})`;
    }
    let m2 = color.match(/^rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-9.]+)\s*\)$/i);
    if (m2) {
        return `rgba(${m2[1]},${m2[2]},${m2[3]},${opacity})`;
    }
    if (!color.startsWith('#')) {
        //todo: handle non hex colors
        return color;
    }
    return color.substring(0, 7) + (255 * opacity).toString(16);
}


interface IBackgroundPositionEditorProps {
    position: string,
    onChange: (s: string) => void
}
export const BackgroundPositionEditor: React.FunctionComponent<IBackgroundPositionEditorProps> = props => {
    let [position, setPosition] = React.useState<string>('center center')

    let xPos = ['left', 'center', 'right']
    let yPos = ['top', 'center', 'bottom']

    React.useEffect(() => {
        if (hasValue(props.position)) {
            let parts = props.position.split(" ", 2)
            console.log("parts ", parts);

            if (parts.length == 2) setPosition(props.position)
            else if (xPos.indexOf(parts[0]) !== -1) setPosition(`${parts[0]} center`)
            else setPosition(`center ${parts[0]}`)

        }
        else setPosition('center center')
    }, [props.position])

    return <div className='uxp_wd-bg-position-editor'>
        <div className="position-editor">
            {xPos.map((x, i) => {
                return yPos.map((y, j) => {
                    let pos = `${x} ${y}`

                    return (
                        <div
                            className={classNames("position", `${x}-${y}`, { 'selected': position == pos })}
                            onClick={() => props.onChange(pos)}
                            key={pos}
                        ></div>
                    )
                })
            })}

        </div>
    </div>
}

interface ICustomColorPicker {
    color: string,
    onChange: (color: string) => void
    defaultColors?: string[],
    contextData: IContextData
}

export let DefaultColors: string[] = [
    //primary
    '#5652C2',
    '#BC59C3',
    '#BB597B',
    '#72C1C7',
    '#ABE087',
    '#C76663',

    //secondary
    '#424242',
    '#F8F8F8',
    '#74C6AB',
    '#A4D6EB',
    '#E7E0FC',
    '#C4EDFC',
    '#F9DAF9',
    '#CCCCF6',
    '#97DBCF',
    '#CFE9AD',
];
export const CustomColorPicker: React.FunctionComponent<ICustomColorPicker> = (props) => {

    const { color, onChange, defaultColors } = props

    // uxp complex picker: palette / manual input / theme variables / presets in one
    // dropdown. Emits the same value formats v4 stored: palette/input -> rgb(a) via
    // returnFormat, presets -> raw hex verbatim, theme variables -> {$.theme.<key>}.
    // Custom defaultColors (per-editor) become the presets; otherwise the uxp
    // defaults (the same v4 DefaultColors list) are used.
    return <div className="uxp_wd-custom-color-picker">
        <ColorPicker
            color={color || ''}
            onChange={(c) => onChange(c)}
            returnFormat="rgb"
            mode="complex"
            enableDefaultPresets={true}
            presets={defaultColors && defaultColors.length > 0 ? defaultColors : undefined}
            defaultTab="presets"
        />
    </div>
}


export interface IColorForRange {
    from: number,
    color: string
}

export interface IColorPickerDialog {
    show: boolean,
    colorForRange: IColorForRange,
    onChange: (colorForRange: IColorForRange) => void,
    onClose: () => void,
    triggerButtonRef: React.MutableRefObject<HTMLElement>
}

export const ColorPickerDialog: React.FunctionComponent<IColorPickerDialog> = (props) => {

    const [coords, setCoords] = React.useState<any>({})

    const minWidth = 250, minHeight = 300
    const adjustBounds = () => {
        const buttonDetails = props.triggerButtonRef.current.getBoundingClientRect();

        const { top, left, right, bottom, width, height } = buttonDetails
        const { innerWidth, innerHeight } = window

        let _coords: any = {}
        let vPos = 'top'
        let hPos = 'left'

        if (top + minHeight > innerHeight) vPos = 'bottom'
        if (left + minWidth > innerWidth) hPos = 'right'

        if (vPos == 'top') _coords.top = top
        else _coords.bottom = innerHeight - bottom

        if (hPos == 'left') _coords.left = left
        else _coords.right = innerWidth - right
        setCoords(_coords);
    }

    React.useLayoutEffect(adjustBounds, [props.show])

    if (props.show) {

        return <PortalContainer
            hasBackdrop={true}
            disableScroll={true}
            onClickBackdrop={() => props.onClose()}
        >
            <div className={classNames("uxp-color-picker-content")} style={coords} >
                <div className="toolbar">
                    <div className="title">Select a color for range</div>

                    <div className="close-btn" onClick={() => props.onClose()}>
                        <div className="icon-cont">
                            <div className="icon"></div>
                        </div>
                    </div>

                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
                    <Label>Change progress bar color to this color from </Label>
                    <Input
                        type="number"
                        value={String(props.colorForRange.from)}
                        onChange={s => {
                            props.onChange({ ...props.colorForRange, from: Number(s) })
                        }}
                        className="widget-designer-input"
                    />
                </div>

                <ColorPallet
                    color={props.colorForRange?.color}
                    onChange={s => {
                        props.onChange({ ...props.colorForRange, color: s })
                        props.onClose()
                    }}
                    onCancel={() => props.onClose()}
                />
            </div>
        </PortalContainer>

    } else {
        return <></>
    }
}

export interface IColorsForMultipleRangesPicker {
    colorsForRanges: IColorForRange[],
    onChange: (colorsForRanges: IColorForRange[]) => void
}

export const ColorsForMultipleRangesPicker: React.FunctionComponent<IColorsForMultipleRangesPicker> = (props) => {

    let { colorsForRanges, onChange } = props
    let [selectedColorForRangeIndex, setSelectedColorForRangeIndex] = React.useState<number>()

    let addButtonRef = React.useRef(null)

    const [showColorPicker, setShowColorPicker] = React.useState<boolean>(false)

    function renderPicker() {
        return (
            <ColorPickerDialog
                onClose={() => {
                    setShowColorPicker(false)
                    setSelectedColorForRangeIndex(null)
                }}
                show={showColorPicker}
                onChange={(v) => {
                    let d = [...colorsForRanges]
                    d[selectedColorForRangeIndex] = v
                    onChange(d)
                }}
                triggerButtonRef={addButtonRef}
                colorForRange={colorsForRanges[selectedColorForRangeIndex]}
            />
        )
    }


    return <div className="uxp_wd-color-range-picker">
        <div className="colors-container" ref={addButtonRef}>

            <HorizontalScrollList
                items={colorsForRanges}
                renderItem={(c, index) => {
                    return (
                        <div
                            style={{ backgroundColor: c?.color }}
                            className={classNames("color-icon color", { 'selected': selectedColorForRangeIndex == index })}
                            onClick={() => {
                                setSelectedColorForRangeIndex(index)
                                setShowColorPicker(true)
                            }}>
                            <span className="text" style={{ color: c?.color }}>
                                {c?.from + "%"}
                            </span>
                        </div>
                    )
                }}
                className="wd-horizontal-scroll-list-small"
            />

            {(!!selectedColorForRangeIndex || selectedColorForRangeIndex == 0) && renderPicker()}

            <div className="add-new-color-range"
                onClick={() => {
                    let from = Math.max(...colorsForRanges.map((v) => v.from)) + 1
                    onChange([...colorsForRanges, { from, color: DefaultColors[colorsForRanges.length % DefaultColors.length] }])
                }}
            >
                <Tooltip position='bottom' content={'Add new color range'}>
                    <div className="add-new-color-range-icon" style={{ backgroundColor: makeColorTransparent('#121212', 0.4) }}>
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                    </div>
                </Tooltip>
            </div>

        </div>

    </div>
}

let TextAlignDirections: string[] = ['left', 'center', 'right', 'justify']
interface ITextAlignEditorProps {
    value: string,
    onChange: (value: string) => void
}
export const TextAlignEditor: React.FunctionComponent<ITextAlignEditorProps> = (props) => {

    let { value, onChange } = props

    return <div className="uxp_wd-text-align-editor">
        {TextAlignDirections.map((d, i) => {

            let iconName = 'align-' + d as IconName
            return <div key={i}
                className={classNames("direction", { "selected": value == d })}
                onClick={() => { onChange(d) }}
            >
                <FontAwesomeIcon icon={['fas', iconName]} />
            </div>
        })}
    </div>
}

export function escapeUnit(value: string, unit: string) {
    return value.replace(unit, '').trim()
}

export interface ITypeSelectorType {
    icon: string,
    value: string,
    label?: string
}
export const TypeSelectorComponent: React.FunctionComponent<{ types: ITypeSelectorType[], selected: string, onChange: (value: string) => void, itemWidth: string, iconStyles?: React.CSSProperties }> = (props) => {
    const iconStyles:React.CSSProperties = props.iconStyles || {}
    
    return <div className={classNames('uxp_wd-type-selector')}>
        {props.types.map((t, i) => {
            return <div
                className={classNames('uxp_wd-type-selector__type', { 'selected': props.selected == t.value })}
                onClick={() => props.onChange(t.value)}
            >
                <div className="uxp_wd-type-selector__content">
                    <div className='label'>{t.label || ''}</div>
                    <FontAwesomeIcon 
                        style={{ width: props.itemWidth, ...iconStyles }} 
                        icon={generateIconProp(t.icon)} 
                    />
                </div>
            </div>
        })}
    </div>
}